<script setup lang="ts">
import { getTaxonTree } from "@carnet/api/backoffice";
import type { SpecieTaxonTreeResource } from "@carnet/api/backoffice";
import { normalizeTaxonIds, buildTaxonLabelMap } from "@carnet/ui/utils/taxon";
import { SPECIE_LEVEL_CONFIRMED } from "@carnet/ui/utils/specieLevel";
import { accordionSectionsWithSelection } from "@carnet/ui/utils/accordionFilters";
import { orderChipsBySections, type AppFilterChipEntry } from "@carnet/ui/utils/filterChips";
import type { UrlFilterValues } from "@/composables/useUrlFilters";

const appliedModel = defineModel<UrlFilterValues>("applied", { default: () => ({}) });

// `memory` : état local sans URL (modale d'observation, qui réserve déjà `?modal=`).
// `excludeKeys` : sections masquées de bout en bout (accordéon + chips), ex. bassin forcé par le site.
const props = withDefaults(defineProps<{ persistence?: "url" | "memory"; initial?: UrlFilterValues; excludeKeys?: string[] }>(), {
  persistence: "url",
  initial: undefined,
  excludeKeys: () => [],
});

const { t } = useI18n();
const globalData = await useGlobalData();

const { data: profile } = useProfileData();
const isNaturalist = computed(() => profile.value?.isNaturalist ?? false);

const { data: taxonsData } = await getTaxonTree({
  composable: "useLazyFetch",
  key: "taxon-tree",
  path: { locale: appLocale() },
  headers: { Accept: "application/json" },
  asyncDataOptions: { server: false },
});

const SECTION_KEYS = ["zones", "taxons", "specieFeatures", "specieLevels"] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const { applied, draft, apply, clearDraft, clearAppliedKey, syncDraftFromApplied, toggleDraft, setDraftValues, isDraftSelected } =
  props.persistence === "memory" ? useAppMemoryFilters(props.initial) : useUrlFilters(() => SECTION_KEYS as readonly string[]);

watch(applied, (v) => (appliedModel.value = v), { immediate: true });

const taxonRoots = computed<SpecieTaxonTreeResource[]>(() => (taxonsData.value ?? []).map(normalizeTaxonIds));
const taxonLabelMap = computed(() => buildTaxonLabelMap(taxonRoots.value));
const taxonNameMap = await useTaxonNames(() => applied.value.taxons ?? []);

const sections = computed(() =>
  [
    { key: "zones" as const, title: t("species.filters.zones.title"), items: globalData.value?.zones ?? [] },
    { key: "taxons" as const, title: t("species.filters.taxons.title"), items: [] },
    { key: "specieFeatures" as const, title: t("species.filters.specieFeatures.title"), items: globalData.value?.specieFeatures ?? [] },
    { key: "specieLevels" as const, title: t("species.filters.specieLevels.title"), items: globalData.value?.specieLevels ?? [] },
  ].filter((s) => !props.excludeKeys.includes(s.key) && (s.key !== "specieLevels" || isNaturalist.value))
);

const draftSpecieLevel = computed(() => draft.value.specieLevels?.[0] ?? SPECIE_LEVEL_CONFIRMED);

const accordionItems = computed(() =>
  sections.value
    .filter((s) => (s.key === "taxons" ? taxonRoots.value.length > 0 : s.items.length > 0))
    .map((s) => ({ value: s.key, label: s.title, slot: s.key as string }))
);

const defaultOpenSections = computed(() => accordionSectionsWithSelection(applied.value, accordionItems.value, ["specieLevels"]));

const open = ref(false);

watch(open, (isOpen) => {
  if (isOpen) syncDraftFromApplied();
});

function onApply() {
  apply();
  open.value = false;
}

function chipLabel(key: SectionKey, code: string): string {
  if (key === "taxons") return taxonLabelMap.value[code] ?? taxonNameMap.value[code] ?? code;
  const list = (globalData.value?.[key] ?? []) as Array<{ code: string; label: string }>;
  return list.find((item) => item.code === code)?.label ?? code;
}

const appliedEntries = computed<AppFilterChipEntry[]>(() =>
  (Object.entries(applied.value) as [SectionKey, string[]][])
    .filter(([key]) => !props.excludeKeys.includes(key) && key !== "specieLevels")
    .flatMap(([key, values]) => values.map((value) => ({ key, value, label: chipLabel(key, value), removable: true })))
);

const appliedSpecieLevel = computed(() => applied.value.specieLevels?.[0] ?? SPECIE_LEVEL_CONFIRMED);
const radioEntries = computed<AppFilterChipEntry[]>(() =>
  isNaturalist.value && !props.excludeKeys.includes("specieLevels")
    ? [{ key: "specieLevels", label: chipLabel("specieLevels", appliedSpecieLevel.value), removable: false }]
    : []
);

const chipEntries = computed(() =>
  orderChipsBySections(
    [...appliedEntries.value, ...radioEntries.value],
    sections.value.map((s) => s.key)
  )
);

const route = useRoute();
const router = useRouter();
if (props.persistence === "url") {
  watch(
    [isNaturalist, () => route.query.specieLevels],
    ([naturalist, raw]) => {
      if (naturalist || raw == null) return;
      const query = { ...route.query };
      delete query.specieLevels;
      router.replace({ query });
    },
    { immediate: true }
  );
}
</script>

<template>
  <WebFilters v-model:open="open" :empty="accordionItems.length === 0" @apply="onApply" @clear="clearDraft">
    <template #chips>
      <AppFilterChips :chips="chipEntries" @remove="clearAppliedKey" />
    </template>

    <UAccordion :items="accordionItems" :default-value="defaultOpenSections" type="multiple">
      <template #taxons-body>
        <AppTaxonTree :nodes="taxonRoots" :selected="draft.taxons ?? []" @update:selected="setDraftValues('taxons', $event)" />
      </template>

      <template v-for="section in sections.filter((s) => s.key !== 'taxons')" :key="section.key" #[`${section.key}-body`]>
        <ul class="flex flex-col">
          <li v-for="item in section.items" :key="item.code">
            <label class="flex items-start gap-3 py-2 text-base cursor-pointer select-none">
              <input
                v-if="section.key === 'specieLevels'"
                type="radio"
                class="accent-blue-700 size-4"
                :name="`filter-${section.key}`"
                :checked="draftSpecieLevel === item.code"
                @change="setDraftValues(section.key, [item.code])"
              />
              <input
                v-else
                type="checkbox"
                class="accent-blue-700 size-4 rounded-xs my-px"
                :checked="isDraftSelected(section.key, item.code)"
                @change="toggleDraft(section.key, item.code)"
              />
              <span>{{ item.label }}</span>
            </label>
          </li>
        </ul>
      </template>
    </UAccordion>
  </WebFilters>
</template>
